import React from 'react';
import { Deserializer } from 'jsonapi-serializer';
import Raven from 'raven-js';

import ReconnectingWebSocket from 'containers/ReconnectingWebSocket';

import ChatRooms from 'api/resources/chatRooms';
import getErrors from 'utils/getErrors';

import Chats from 'chats/Chats';

const PAGE_SIZE = 25;

export class ChatsContainer extends React.PureComponent {
  state = {
    chatRoomsByHash: {},
    errors: [],
    loading: true,
    next: false,

    activeChatRoom: undefined,

    roomType: 'tech_chat',
  }

  componentDidMount() {
    this.reloadChatRooms();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.roomType !== prevState.roomType) {
      this.reloadChatRooms();
    }

    // обновилась активная чат-комната
    if ((
      this.state.activeChatRoom &&
      this.state.chatRoomsByHash[this.state.activeChatRoom.id]
      !== prevState.chatRoomsByHash[this.state.activeChatRoom.id]
    )) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(innerPrevState => ({
        activeChatRoom: innerPrevState.chatRoomsByHash[innerPrevState.activeChatRoom.id],
      }));
    }
  }

  componentWillUnmount() {
    if (this.abortController) this.abortController.abort();
  }

  get chatRooms() {
    return Object.values(this.state.chatRoomsByHash).sort((a, b) => {
      if (a.lastActivityAt < b.lastActivityAt) return 1;
      if (a.lastActivityAt > b.lastActivityAt) return -1;
      return 0;
    });
  }

  handleChatsMessagesMessage = async (response) => {
    if (!response || !response.data) return;

    let newMessage;
    try {
      newMessage = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);
    } catch (err) {
      Raven.captureException(err, { extra: this.props });
      return;
    }

    this.handleMessageSended(newMessage);
  }

  handleAdminNotificationsMessage = (response) => {
    if (!response || !response.action) return;

    switch (response.action) {
      case 'lock':
      case 'unlock':
        this.handleLockAndUnlock(response);
        break;
      case 'move_cursor':
        this.handleMoveCursor(response);
        break;
      default:
        Raven.captureException('Unsupported WebSocket action', { extra: response });
        break;
    }
  }

  handleLockAndUnlock = (response) => {
    if (!response || !response.body || response.type !== 'chat_room') return;
    if (!this.state.chatRoomsByHash[response.body.id]) return;

    this.setState(prevState => ({
      chatRoomsByHash: {
        ...prevState.chatRoomsByHash,
        [response.body.id]: {
          ...prevState.chatRoomsByHash[response.body.id],
          locked: response.body.locked,
          lockedEmployeeFullname: response.body.locked_employee_fullname,
          lockedEmployeeId: response.body.locked_employee_id,
        },
      },
    }));
  }

  handleMoveCursor = (response) => {
    if (!response || !response.body || response.type !== 'chat_room') return;
    if (!this.state.chatRoomsByHash[response.body.id]) return;

    this.setState(prevState => ({
      chatRoomsByHash: {
        ...prevState.chatRoomsByHash,
        [response.body.id]: {
          ...prevState.chatRoomsByHash[response.body.id],
          hasNewMessages: response.body.has_new_messages,
        },
      },
    }));
  }

  handleReloadClick = () => {
    this.reloadChatRooms();
  }

  handleChatRoomOpenClose = () => {
    this.setState({ activeChatRoom: undefined });
  }

  handleChatRoomSelect = (chatRoomId) => {
    this.setState(prevState => ({ activeChatRoom: prevState.chatRoomsByHash[chatRoomId] }));
  }

  handleTabChange = (activeTab) => {
    this.setState({ activeChatRoom: undefined, roomType: activeTab.filter });
  }

  handleWaypointEnter = () => {
    this.loadChatRooms();
  }

  handleChatRoomStarted = (newChatRoom) => {
    this.setState(prevState => ({
      chatRoomsByHash: { ...prevState.chatRoomsByHash, [newChatRoom.id]: newChatRoom },
    }));
  }

  handleChatRoomFinished = (newChatRoom) => {
    this.setState(prevState => ({
      chatRoomsByHash: { ...prevState.chatRoomsByHash, [newChatRoom.id]: newChatRoom },
    }));
  }

  handleChatRoomRead = (newChatRoom) => {
    this.setState(prevState => ({
      chatRoomsByHash: { ...prevState.chatRoomsByHash, [newChatRoom.id]: newChatRoom },
    }));
  }

  handleMessageSended = (newMessage) => {
    if (!newMessage || !newMessage.chatRoom) return;
    if (newMessage.chatRoom.roomType !== this.state.roomType) return;

    this.setState(prevState => ({
      chatRoomsByHash: {
        ...prevState.chatRoomsByHash,
        [newMessage.chatRoom.id]: {
          ...prevState.chatRoomsByHash[newMessage.chatRoom.id],
          ...newMessage.chatRoom,
          user: {
            ...(prevState.chatRoomsByHash[newMessage.chatRoom.id] &&
              prevState.chatRoomsByHash[newMessage.chatRoom.id].user
            ),
            ...newMessage.user,
          },
          lastMessage: newMessage,
        },
      },
    }));
  }

  handleUserBan = (user) => {
    // TODO: activeChatRoom может быть уже не актуальна, передавать явно
    this.setState(prevState => ({
      chatRoomsByHash: {
        ...prevState.chatRoomsByHash,
        [prevState.activeChatRoom.id]: {
          ...prevState.chatRoomsByHash[prevState.activeChatRoom.id],
          user: { ...prevState.chatRoomsByHash[prevState.activeChatRoom.id].user, ...user },
        },
      },
    }));
  }

  loadChatRooms = async () => {
    if (this.abortController) this.abortController.abort();
    if (typeof window.AbortController === 'function') {
      this.abortController = new window.AbortController();
    }

    this.setState({ errors: [], loading: true });

    const lastActivityAt = Object.values(this.state.chatRoomsByHash).reduce((min, chatRoom) => (
      min > chatRoom.lastActivityAt ? chatRoom.lastActivityAt : min
    ), Number.MAX_SAFE_INTEGER);

    try {
      const response = await ChatRooms.getAll({
        page: { last_activity_at: lastActivityAt, size: PAGE_SIZE },
        filter: { room_type: this.state.roomType },
        include: 'last_message,user,user.avatar,user.oauth_providers,user.current_ban',
        fields: {
          user: 'first_name,last_name,avatar,oauth_providers,current_ban',
        },
        signal: this.abortController ? this.abortController.signal : undefined,
      });

      const chatRooms = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState(prevState => ({
        chatRoomsByHash: chatRooms.reduce((acc, chatRoom) => (
          { ...acc, [chatRoom.id]: chatRoom }
        ), prevState.chatRoomsByHash),
        loading: false,
        next: chatRooms.length === PAGE_SIZE,
      }));
    } catch (err) {
      if (err.name === 'AbortError') return;

      const errors = await getErrors(err);
      this.setState({ errors, loading: false });

      Raven.captureException(err, { extra: this.props });
    }
  }

  reloadChatRooms = async () => {
    if (this.abortController) this.abortController.abort();
    if (typeof window.AbortController === 'function') {
      this.abortController = new window.AbortController();
    }

    this.setState({
      chatRoomsByHash: {},
      errors: [],
      loading: true,
      next: false,
    });

    try {
      const response = await ChatRooms.getAll({
        page: { size: PAGE_SIZE },
        filter: { room_type: this.state.roomType },
        include: 'last_message,user,user.avatar,user.oauth_providers,user.current_ban',
        fields: {
          user: 'first_name,last_name,avatar,oauth_providers,current_ban',
        },
        signal: this.abortController ? this.abortController.signal : undefined,
      });

      const chatRooms = await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(response);

      this.setState({
        chatRoomsByHash: chatRooms.reduce((acc, chatRoom) => ({ ...acc, [chatRoom.id]: chatRoom }), {}),
        loading: false,
        next: chatRooms.length === PAGE_SIZE,
      });
    } catch (err) {
      if (err.name === 'AbortError') return;

      const errors = await getErrors(err);
      this.setState({ errors, loading: false });

      Raven.captureException(err, { extra: this.props });
    }
  }

  // вызывается снаружи
  loadAndOpenModeratorChatForUser = ({ room }) => {
    this.setState({ activeChatRoom: room, roomType: 'moderator_chat' });
  }

  render() {
    return (
      <React.Fragment>
        <ReconnectingWebSocket onMessage={this.handleAdminNotificationsMessage} path="/_ws/c/admin/notifications" />

        <ReconnectingWebSocket onMessage={this.handleChatsMessagesMessage} path="/_ws/c/chats/messages">
          {socket => (
            <Chats
              chatRooms={this.chatRooms}
              errors={this.state.errors}
              loading={this.state.loading}
              next={this.state.next}

              activeChatRoom={this.state.activeChatRoom}
              roomType={this.state.roomType}

              onWaypointEnter={this.handleWaypointEnter}
              onReloadClick={this.handleReloadClick}
              onTabChange={this.handleTabChange}
              onChatRoomSelect={this.handleChatRoomSelect}
              onChatRoomOpenClose={this.handleChatRoomOpenClose}
              onChatRoomStarted={this.handleChatRoomStarted}
              onChatRoomFinished={this.handleChatRoomFinished}
              onBan={this.handleUserBan}
              onMessageSended={this.handleMessageSended}
              onChatRoomRead={this.handleChatRoomRead}

              socket={socket}
            />
          )}
        </ReconnectingWebSocket>
      </React.Fragment>
    );
  }
}

export default ChatsContainer;
